import { useMutation, useQueryClient } from '@tanstack/react-query';
import useApi from '../../../api/useApi';
import { useAsset } from '../../../../hooks/stateful/useAsset';
import useDownloadModifiedAsset from '../../../../hooks/useDownloadModifiedAsset';
import { LOAD_QUERY_KEYS } from '../../../api/load/keys';
import acquisitionTypes from '../../../../support/acquisitionTypes';

export type BatchDownloadConfig = {
  options?: Array<{ id?: number; agreementType?: string }>;
  [key: string]: unknown;
};

export type OnLoadResult = {
  configs?: Record<string, BatchDownloadConfig | undefined>;
};

const onLoadDataKeys = LOAD_QUERY_KEYS.root();

type LoadServiceParams = {
  acquisitionType: string;
  availableAcquisitionTypes: string[];
  assetId?: string;
  assetIds?: string[];
  assetType?: string;
  licenseType?: string;
  family?: string;
  genId?: string | null;
  premiumAccessId: string;
};

const useBatchPaAgreementSwitch = () => {
  const { loadService } = useApi() as unknown as {
    loadService: {
      getLoadData: (params: LoadServiceParams) => Promise<OnLoadResult>;
    };
  };
  const queryClient = useQueryClient();
  const { asset, assets } = useAsset() as {
    asset?: {
      id?: string;
      assetType?: string;
      licenseType?: string;
      family?: string;
    };
    assets?: Array<{ id: string }>;
  };
  const { genId } = useDownloadModifiedAsset();

  const onLoadQueryKey = [
    ...onLoadDataKeys,
    asset?.id,
    asset?.assetType,
    genId,
  ];

  const mutation = useMutation({
    mutationFn: (premiumAccessId: string): Promise<OnLoadResult> =>
      loadService.getLoadData({
        acquisitionType: acquisitionTypes.BATCH_DOWNLOAD,
        availableAcquisitionTypes: [acquisitionTypes.BATCH_DOWNLOAD],
        assetId: asset?.id,
        assetIds: assets?.map((singleAsset) => singleAsset.id),
        assetType: asset?.assetType,
        licenseType: asset?.licenseType,
        family: asset?.family,
        genId,
        premiumAccessId,
      }),
    onSuccess: (result: OnLoadResult) => {
      const freshBatchConfig =
        result?.configs?.[acquisitionTypes.BATCH_DOWNLOAD];
      if (!freshBatchConfig) return;

      queryClient.setQueryData(
        onLoadQueryKey,
        (oldData: OnLoadResult | undefined) => {
          if (!oldData) return oldData;
          const existingBatchConfig =
            oldData.configs?.[acquisitionTypes.BATCH_DOWNLOAD];
          return {
            ...oldData,
            configs: {
              ...oldData.configs,
              [acquisitionTypes.BATCH_DOWNLOAD]: {
                ...existingBatchConfig,
                ...freshBatchConfig,
              },
            },
          };
        }
      );
    },
  });

  return {
    switchPremiumAccessAgreement: mutation.mutate,
    isSwitchingPremiumAccessAgreement: mutation.isPending,
    switchingToPremiumAccessId: mutation.variables,
    switchPremiumAccessAgreementError: mutation.error,
  };
};

export default useBatchPaAgreementSwitch;
